import { displayName } from '../../domain/names';
import type { Actor } from '../../domain/permissions';
import { MAX_SOURCE_CHARS, normaliseRecipe, publishProblem, recipeEditRight, recipePictures, sameRecipe, workingRecipe } from '../../domain/recipes';
import type { CommandResult, Recipe, UUID } from '../../domain/types';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { RecipeRepository, SaveRecipeCommand } from '../types';
import { actorOf, denied, guarded, invalid, notFound, okResult, tryActor } from './actor';
import { deleteBlob, mediumKey, thumbKey } from './blobStore';
import type { MockDb } from './MockDb';

/**
 * The family's recipes, on IndexedDB.
 *
 * Three rules do most of the work here, and each of them is somebody else's rule
 * borrowed rather than a new one:
 *
 *   - **A draft is its writer's alone** (a story's rule). Nobody else receives it,
 *     not even the person whose circle it will hang on — a half-dictated recipe
 *     is a private thing.
 *   - **Editing never touches what is out** (nobody's rule, and the reason this
 *     file is not shorter). Once a recipe is published, `save` writes to
 *     `Recipe.draft` and the family goes on reading `Recipe.doc` until somebody
 *     presses Publish. A session with the assistant can take twenty saves and
 *     one of them can be wrong; none of that belongs on a page a cousin may be
 *     cooking from at that moment.
 *   - **Nothing is destroyed** (everybody's rule). A published recipe that is
 *     taken down is archived, and Admin controls → Moderation can put it back.
 *     Only a draft nobody else ever saw is really thrown away.
 *
 * Saves are deliberately *not* written to the audit history one by one. The
 * editor saves after every change, and the history is append-only and meant to
 * be read: what belongs there is that a recipe was started, published, changed
 * for the family, or taken down — not the forty keystrokes in between.
 */
export class MockRecipeRepository implements RecipeRepository {
  constructor(private db: MockDb) {}

  /**
   * What one viewer is allowed to receive of one recipe — or nothing.
   *
   * `me` is the signed-in actor when the viewer *is* the signed-in account, which
   * is the only case the prototype has; anybody else is treated as an ordinary
   * reader, the safe answer.
   */
  private seenBy(r: Recipe, accountId: UUID, me: Actor | null): Recipe | null {
    if (r.status === 'draft') return r.creatorAccountId === accountId ? r : null;
    // Taken down: kept for the administrators, who are the ones who can put it back.
    if (r.status === 'archived') return me?.session.role === 'admin' ? r : null;
    // Unpublished changes, and the raw telling, are only for whoever can act on them.
    const mayEdit = !!me && recipeEditRight(me, r) !== 'none';
    return mayEdit ? r : { ...r, draft: undefined, source: undefined };
  }

  private viewer(accountId: UUID): Actor | null {
    const actor = tryActor(this.db);
    return actor && actor.session.accountId === accountId ? actor : null;
  }

  async visibleTo(accountId: UUID): Promise<Recipe[]> {
    await this.db.read();
    const me = this.viewer(accountId);
    return this.db.t.recipes
      .map((r) => this.seenBy(r, accountId, me))
      .filter((r): r is Recipe => !!r)
      .sort((a, b) => (b.publishedAt ?? b.updatedAt).localeCompare(a.publishedAt ?? a.updatedAt));
  }

  async get(id: UUID): Promise<Recipe | undefined> {
    await this.db.read();
    const session = this.db.getSession();
    const row = this.db.t.recipes.find((r) => r.id === id);
    if (!row || !session) return undefined;
    return this.seenBy(row, session.accountId, this.viewer(session.accountId)) ?? undefined;
  }

  async save(cmd: SaveRecipeCommand): Promise<CommandResult<Recipe>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const doc = normaliseRecipe(cmd.doc);
      const source = cmd.source?.trim().slice(0, MAX_SOURCE_CHARS) || undefined;
      const now = nowISO();
      const existing = db.t.recipes.find((r) => r.id === cmd.recipeId);

      if (!existing) {
        const prior = db.idem(cmd.idempotencyKey);
        if (prior) {
          const row = db.t.recipes.find((r) => r.id === prior.entityId);
          if (row) return okResult(row, { noop: true });
        }
        const person = db.t.persons.find((p) => p.id === cmd.personId && !p.archived && !p.mergedIntoId);
        if (!person) return invalid('recipe.error.needPerson');
        const recipe: Recipe = {
          id: cmd.recipeId,
          familyId: actor.session.familyId,
          personId: person.id,
          creatorAccountId: actor.session.accountId,
          lang: cmd.lang,
          status: 'draft',
          doc,
          source,
          idempotencyKey: cmd.idempotencyKey,
          version: 1,
          createdAt: now,
          updatedAt: now,
          updatedBy: actor.session.accountId,
        };
        db.t.recipes.push(recipe);
        db.rememberIdem(cmd.idempotencyKey, 'recipe', recipe.id);
        db.audit({ actorAccountId: actor.session.accountId, entityType: 'recipe', entityId: recipe.id, action: 'create', description: 'Started writing down a recipe', entityVersion: 1 });
        await db.commit(['recipes', 'idempotency', 'audit_events'], [recipe.id]);
        return okResult(recipe);
      }

      if (existing.status === 'archived') return notFound();
      if (recipeEditRight(actor, existing) === 'none') return denied('recipe.error.notYours');
      if (cmd.expectedVersion !== undefined && cmd.expectedVersion !== existing.version) {
        return {
          ok: false,
          kind: 'conflict',
          conflicts: [{ field: 'doc', currentValue: workingRecipe(existing), yourValue: doc, baseValue: undefined, changedByAccountId: existing.updatedBy, changedAt: existing.updatedAt }],
          current: existing,
          currentVersion: existing.version,
        };
      }

      if (existing.status === 'draft') {
        existing.doc = doc;
        // Whose it is can still be put right while nobody else has seen it.
        if (cmd.personId !== existing.personId && db.t.persons.some((p) => p.id === cmd.personId && !p.archived && !p.mergedIntoId)) existing.personId = cmd.personId;
      } else {
        // Already out: the family keeps reading `doc` until Publish is pressed.
        existing.draft = sameRecipe(doc, existing.doc) ? undefined : doc;
      }
      if (source) existing.source = source;
      existing.lang = cmd.lang;
      existing.version += 1;
      existing.updatedAt = now;
      existing.updatedBy = actor.session.accountId;
      await db.commit(['recipes'], [existing.id]);
      return okResult(existing);
    });
  }

  async publish(recipeId: UUID, expectedVersion: number, idempotencyKey: string): Promise<CommandResult<Recipe>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const recipe = db.t.recipes.find((r) => r.id === recipeId);
      if (!recipe || recipe.status === 'archived') return notFound();
      if (db.idem(idempotencyKey)) return okResult(recipe, { noop: true });
      const right = recipeEditRight(actor, recipe);
      if (right === 'none') return denied('recipe.error.notYours');
      if (expectedVersion !== recipe.version) {
        return { ok: false, kind: 'conflict', conflicts: [], current: recipe, currentVersion: recipe.version };
      }
      const doc = workingRecipe(recipe);
      const problem = publishProblem(doc);
      if (problem) return invalid(problem === 'title' ? 'recipe.error.needTitle' : 'recipe.error.needBody');

      const first = recipe.status === 'draft';
      const now = nowISO();
      recipe.doc = doc;
      recipe.draft = undefined;
      recipe.status = 'published';
      recipe.publishedAt = recipe.publishedAt ?? now;
      recipe.version += 1;
      recipe.updatedAt = now;
      recipe.updatedBy = actor.session.accountId;
      db.rememberIdem(idempotencyKey, 'recipe', recipe.id);
      db.audit({
        actorAccountId: actor.session.accountId,
        entityType: 'recipe',
        entityId: recipe.id,
        action: first ? 'publish' : 'update',
        onBehalfOfPersonId: recipe.personId,
        description: first ? `Shared a recipe: “${doc.title}”` : `Changed the recipe “${doc.title}”`,
        entityVersion: recipe.version,
      });

      // The family hears about a recipe once, when it arrives — not every time a pinch of salt is corrected.
      if (first) {
        const who = db.accountName(actor.session.accountId);
        const cook = db.personWithNames(recipe.personId);
        const writerIsCook = db.accountsForPerson(recipe.personId).includes(actor.session.accountId);
        const cookEn = cook ? displayName(cook, 'en') : '';
        const cookHe = cook ? displayName(cook, 'he') : '';
        db.notifyFamily(actor.session.accountId, {
          kind: 'recipe',
          actorAccountId: actor.session.accountId,
          title: writerIsCook || !cook ? `${who} shared a recipe: “${doc.title}”` : `${who} wrote down ${cookEn}’s recipe: “${doc.title}”`,
          titleHe: writerIsCook || !cook ? `${who} שיתף/ה מתכון: "${doc.title}"` : `${who} כתב/ה את המתכון של ${cookHe}: "${doc.title}"`,
          link: `/recipes/${recipe.id}`,
        });
      }
      await db.commit(['recipes', 'idempotency', 'audit_events', 'notifications'], [recipe.id, recipe.personId]);
      return okResult(recipe);
    });
  }

  async discardChanges(recipeId: UUID): Promise<CommandResult<Recipe>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const recipe = this.db.t.recipes.find((r) => r.id === recipeId);
      if (!recipe || recipe.status !== 'published') return notFound();
      if (recipeEditRight(actor, recipe) === 'none') return denied('recipe.error.notYours');
      if (!recipe.draft) return okResult(recipe, { noop: true });
      recipe.draft = undefined;
      recipe.version += 1;
      recipe.updatedAt = nowISO();
      recipe.updatedBy = actor.session.accountId;
      await this.db.commit(['recipes'], [recipe.id]);
      return okResult(recipe);
    });
  }

  async remove(recipeId: UUID, expectedVersion: number, reason?: string): Promise<CommandResult<{ removed: true }>> {
    return guarded(this.db, async () => {
      const db = this.db;
      const actor = actorOf(db);
      const recipe = db.t.recipes.find((r) => r.id === recipeId);
      if (!recipe || recipe.status === 'archived') return notFound();
      if (recipeEditRight(actor, recipe) === 'none') return denied('recipe.error.notYours');
      if (expectedVersion !== recipe.version) return { ok: false, kind: 'conflict', conflicts: [], current: { removed: true }, currentVersion: recipe.version };

      if (recipe.status === 'draft') {
        // Nobody but its writer ever saw it — or the pictures they put on it, which
        // were hidden from the family along with it (MockPhotoRepository.list). So there is
        // nothing for anybody to lose, and they go with it rather than being left
        // pointing at a recipe that is not there.
        const pictures = db.t.family_photos.filter((p) => p.addedTo?.kind === 'recipe' && p.addedTo.id === recipeId);
        db.t.recipes = db.t.recipes.filter((r) => r.id !== recipeId);
        db.t.family_photos = db.t.family_photos.filter((p) => !pictures.includes(p));
        for (const p of pictures) {
          if (p.storageKey) await deleteBlob(p.storageKey).catch(() => {});
          await deleteBlob(thumbKey(p.id)).catch(() => {});
          await deleteBlob(mediumKey(p.id)).catch(() => {});
        }
        await db.commit(pictures.length ? ['recipes', 'family_photos'] : ['recipes'], [recipeId]);
        return okResult({ removed: true as const });
      }
      recipe.status = 'archived';
      recipe.version += 1;
      recipe.updatedAt = nowISO();
      recipe.updatedBy = actor.session.accountId;
      db.audit({
        actorAccountId: actor.session.accountId,
        entityType: 'recipe',
        entityId: recipe.id,
        action: 'archive',
        description: `Took down the recipe “${recipe.doc.title}” (kept, and recoverable)`,
        reason: reason?.trim() || undefined,
        entityVersion: recipe.version,
      });
      await db.commit(['recipes', 'audit_events'], [recipe.id, recipe.personId]);
      return okResult({ removed: true as const });
    });
  }

  async restore(recipeId: UUID): Promise<CommandResult<Recipe>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (actor.session.role !== 'admin') return denied();
      const recipe = this.db.t.recipes.find((r) => r.id === recipeId);
      if (!recipe) return notFound();
      if (recipe.status !== 'archived') return okResult(recipe, { noop: true });
      recipe.status = 'published';
      recipe.version += 1;
      recipe.updatedAt = nowISO();
      recipe.updatedBy = actor.session.accountId;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'recipe', entityId: recipe.id, action: 'restore', description: `Put the recipe “${recipe.doc.title}” back`, entityVersion: recipe.version });
      await this.db.commit(['recipes', 'audit_events'], [recipe.id, recipe.personId]);
      return okResult(recipe);
    });
  }

  async setCover(recipeId: UUID, photoId: UUID | null): Promise<CommandResult<Recipe>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const recipe = this.db.t.recipes.find((r) => r.id === recipeId);
      if (!recipe || recipe.status === 'archived') return notFound();
      if (recipeEditRight(actor, recipe) === 'none') return denied('recipe.error.notYours');
      // Only a picture the family really added to this recipe, and one that is still there to be seen.
      if (photoId && !recipePictures(this.db.t.family_photos, recipe.id).some((p) => p.id === photoId)) return notFound();
      const next = photoId ?? undefined;
      if (recipe.coverPhotoId === next) return okResult(recipe, { noop: true });
      // `version` stays where it is on purpose — see RecipeRepository.setCover.
      recipe.coverPhotoId = next;
      await this.db.commit(['recipes'], [recipe.id]);
      return okResult(recipe);
    });
  }

  async react(recipeId: UUID, emoji: string | null): Promise<CommandResult<Recipe>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const recipe = this.db.t.recipes.find((r) => r.id === recipeId);
      if (!recipe) return notFound();
      if (recipe.status !== 'published') return denied();
      const mine = actor.session.accountId;
      const rest = (recipe.reactions ?? []).filter((r) => r.accountId !== mine);
      recipe.reactions = emoji ? [...rest, { accountId: mine, emoji, createdAt: nowISO() }] : rest;
      await this.db.commit(['recipes'], [recipe.id]);
      // A reader reacting gets back what a reader may see, not the writer's working copy.
      return okResult(this.seenBy(recipe, mine, actor) ?? recipe);
    });
  }
}
